import {
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type ChangeEvent,
} from 'react';
import { palette } from '../../theme';
import { usePortalPalette } from '../../theme/usePortalPalette';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
} from '../../primitives';
import { linkify } from '../chat/linkify';

/** One person the user may @mention. Copy is resolved by the caller. */
export interface MentionSuggestion {
  id: string;
  label: string;
  /** A second line, e.g. their role. */
  hint?: string;
}

/** A mention already in the text — the label is what appears after the `@`. */
export interface MentionRef {
  id: string;
  label: string;
}

/**
 * The `@query` being typed at the caret, or null when the caret is not inside a
 * mention. The `@` must start a word, so an email address never opens the list.
 */
export function mentionQueryAt(text: string, caret: number): string | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1] ?? '')) return null;
  const query = before.slice(at + 1);
  if (/[\n\r]/.test(query) || query.length > 40) return null;
  return query;
}

/** Replace the `@query` at the caret with `@Label `, returning the new text and caret. */
export function insertMention(
  text: string,
  caret: number,
  label: string,
): { text: string; caret: number } {
  const at = text.slice(0, caret).lastIndexOf('@');
  if (at < 0) return { text, caret };
  const token = `@${label} `;
  return { text: `${text.slice(0, at)}${token}${text.slice(caret)}`, caret: at + token.length };
}

/** The suggestions whose label contains the query, case- and accent-insensitively. */
export function filterSuggestions(
  suggestions: readonly MentionSuggestion[],
  query: string,
  limit = 8,
): MentionSuggestion[] {
  const fold = (value: string) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  const q = fold(query);
  return suggestions.filter((s) => fold(s.label).includes(q)).slice(0, limit);
}

export interface MentionTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Everyone mentionable; filtered here against what is typed after the `@`. */
  suggestions: readonly MentionSuggestion[];
  /** A person was picked from the list. The caller keeps track of who. */
  onPick: (suggestion: MentionSuggestion) => void;
  /** Fired as the `@query` changes (null when the list closes), e.g. to fetch lazily. */
  onQueryChange?: (query: string | null) => void;
  /** Passed through to the field after the list has had first refusal. */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  ariaLabel?: string;
  /** Accessible name of the suggestion list. */
  listLabel: string;
  disabled?: boolean;
  minRows?: number;
  maxRows?: number;
  size?: 'small' | 'medium';
  /** Styles for the TextField itself. */
  fieldSx?: object;
  inputTestId?: string;
}

/**
 * A multiline text field that offers colleagues to @mention.
 *
 * Typing `@` at the start of a word opens a list of the caller-supplied
 * suggestions filtered by what follows; arrows move, Enter or Tab picks, Escape
 * closes. Picking writes `@Name ` into the text and reports the person to the
 * caller, who sends their id alongside the text — the visible name is for the
 * reader, the id is what the server notifies.
 */
export function MentionTextField({
  value,
  onChange,
  suggestions,
  onPick,
  onQueryChange,
  onKeyDown,
  onBlur,
  label,
  placeholder,
  ariaLabel,
  listLabel,
  disabled,
  minRows,
  maxRows = 8,
  size = 'small',
  fieldSx,
  inputTestId,
}: MentionTextFieldProps) {
  const portal = usePortalPalette();
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  const matches = query === null ? [] : filterSuggestions(suggestions, query);
  const open = matches.length > 0;
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const anchorRef = useRef<HTMLDivElement | null>(null);

  function updateQuery(text: string, caret: number) {
    const next = mentionQueryAt(text, caret);
    if (next !== query) {
      setQuery(next);
      setActive(0);
      onQueryChange?.(next);
    }
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const text = event.target.value;
    onChange(text);
    updateQuery(text, event.target.selectionStart ?? text.length);
  }

  function pick(suggestion: MentionSuggestion) {
    const caret = inputRef.current?.selectionStart ?? value.length;
    const next = insertMention(value, caret, suggestion.label);
    onChange(next.text);
    onPick(suggestion);
    setQuery(null);
    onQueryChange?.(null);
    // Put the caret after the inserted name once React has applied the value.
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        input.setSelectionRange(next.caret, next.caret);
      }
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((i) => (i + 1) % matches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
        event.preventDefault();
        pick(matches[Math.min(active, matches.length - 1)]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setQuery(null);
        onQueryChange?.(null);
        return;
      }
    }
    onKeyDown?.(event);
  }

  return (
    <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        size={size}
        value={value}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        inputRef={inputRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(event) => {
          const target = event.target as HTMLTextAreaElement;
          updateQuery(value, target.selectionStart ?? value.length);
        }}
        onBlur={() => {
          // Late, so a click on a suggestion lands before the list closes.
          setTimeout(() => setQuery(null), 150);
          onBlur?.();
        }}
        slotProps={{
          htmlInput: {
            'aria-label': ariaLabel ?? label ?? placeholder,
            // A textarea is a textbox, which takes aria-autocomplete,
            // aria-controls and aria-activedescendant but not aria-expanded.
            'aria-autocomplete': 'list',
            ...(open
              ? {
                  'aria-controls': listId,
                  'aria-activedescendant': optionId(Math.min(active, matches.length - 1)),
                }
              : {}),
            ...(inputTestId ? { 'data-testid': inputTestId } : {}),
          },
        }}
        sx={fieldSx}
      />
      {/* In a Popper (portalled) rather than absolutely inside the field: a
          card's overflow and the siblings after it painted over an in-place
          list, so the suggestions could be seen but not clicked. */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        style={{ width: anchorRef.current?.offsetWidth }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 0.5,
            border: `1px solid ${palette.border.line}`,
            borderRadius: 1.5,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          <List id={listId} dense role="listbox" aria-label={listLabel} sx={{ py: 0.5 }}>
            {matches.map((suggestion, index) => (
              <ListItemButton
                key={suggestion.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                selected={index === active}
                // mousedown, not click: the field's blur would otherwise close
                // the list before the click arrives.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(suggestion);
                }}
                sx={{ '&.Mui-selected': { backgroundColor: portal.brand.accentTint } }}
              >
                <ListItemText primary={suggestion.label} secondary={suggestion.hint} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
}

/**
 * Text with its @mentions highlighted and its links made clickable. Mentions
 * are matched longest name first, so "@Avery Tremblay" is not read as
 * "@Avery" followed by text. Rendered as React nodes, never as HTML.
 */
export function MentionText({
  text,
  mentions = [],
}: {
  text: string;
  mentions?: readonly MentionRef[];
}) {
  const portal = usePortalPalette();
  const tokens = [...mentions]
    .filter((m) => m.label)
    .sort((a, b) => b.label.length - a.label.length);
  const nodes: ReactNode[] = [];
  let buffer = '';
  let i = 0;
  const flush = () => {
    if (buffer) nodes.push(...linkify(buffer).map((n, k) => <span key={`t${i}-${k}`}>{n}</span>));
    buffer = '';
  };
  while (i < text.length) {
    const match =
      text[i] === '@' ? tokens.find((m) => text.startsWith(`@${m.label}`, i)) : undefined;
    if (match) {
      flush();
      const token = `@${match.label}`;
      nodes.push(
        <Box
          key={`m${i}`}
          component="span"
          data-mention={match.id}
          sx={{
            fontWeight: 700,
            color: portal.brand.accent,
            backgroundColor: portal.brand.accentTint,
            borderRadius: 0.5,
            px: 0.25,
          }}
        >
          {token}
        </Box>,
      );
      i += token.length;
    } else {
      buffer += text[i];
      i += 1;
    }
  }
  flush();
  return <>{nodes}</>;
}
